import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { useAuth } from './session';

/**
 * Gate its children behind an authenticated session.
 *
 * Three branches:
 *   - `loading`  — initial `/auth/me` is in flight. We render a minimal
 *                  placeholder; actual skeleton UIs belong inside the
 *                  protected routes themselves, not here.
 *   - `anonymous` — redirect to `/login`, preserving the original
 *                   destination in `state.from` so the login page can bounce
 *                   the user back after a successful sign-in.
 *   - `authenticated` — render the children untouched.
 *
 * The redirect uses `replace` so the login page does not add a history
 * entry the user would have to skip on back-navigation.
 */
export interface RequireAuthProps {
  children: ReactNode;
}

export function RequireAuth({ children }: RequireAuthProps): JSX.Element {
  const { status } = useAuth();
  const location = useLocation();

  if (status === 'loading') {
    return (
      <div role="status" aria-live="polite" aria-busy="true">
        Loading session…
      </div>
    );
  }

  if (status === 'anonymous') {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  return <>{children}</>;
}
